/**
 * Retry Utility
 * Provides retry functionality with exponential backoff for improved reliability
 */

interface RetryOptions {
  /**
   * Maximum number of retry attempts
   * @default 3
   */
  maxRetries?: number;
  /**
   * Initial delay in milliseconds before first retry
   * @default 1000
   */
  initialDelay?: number;
  /**
   * Maximum delay in milliseconds (caps exponential backoff)
   * @default 10000
   */
  maxDelay?: number;
  /**
   * Custom function to determine if an error should be retried
   * @param error The error that occurred
   * @returns true if the error should be retried, false otherwise
   */
  shouldRetry?: (error: unknown) => boolean;
}

/**
 * Retry function with exponential backoff
 * 
 * Automatically retries failed operations with increasing delays between attempts.
 * Useful for handling transient network errors, temporary server issues, etc.
 * 
 * @param fn Function to retry (must return a Promise)
 * @param options Retry configuration options
 * @returns Promise that resolves with the function's result
 * 
 * @example
 * ```typescript
 * // Basic usage
 * const result = await retryWithBackoff(
 *   () => fetchProducts(),
 *   { maxRetries: 3, initialDelay: 1000 }
 * );
 * 
 * // With custom retry logic
 * const result = await retryWithBackoff(
 *   () => apiCall(),
 *   {
 *     maxRetries: 5,
 *     initialDelay: 500,
 *     shouldRetry: (error) => {
 *       // Only retry on network errors, not 4xx errors
 *       return error instanceof TypeError || 
 *              (error as any)?.status >= 500;
 *     }
 *   }
 * );
 * ```
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    shouldRetry = () => true, // Retry all errors by default
  } = options;

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Check if we should retry this error
      if (!shouldRetry(error)) {
        throw lastError;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        throw lastError;
      }

      // Calculate delay with exponential backoff
      // Formula: initialDelay * 2^attempt, capped at maxDelay
      const delay = Math.min(
        initialDelay * Math.pow(2, attempt),
        maxDelay
      );

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError || new Error('Retry failed');
}

/**
 * Retry function with exponential backoff (shorthand)
 * 
 * Convenience function with default options for common use cases.
 * 
 * @param fn Function to retry
 * @param maxRetries Maximum number of retries (default: 3)
 * @param initialDelay Initial delay in milliseconds (default: 1000)
 * @returns Promise that resolves with the function's result
 */
export async function retry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  return retryWithBackoff(fn, { maxRetries, initialDelay });
}

